import { useRef, useState, useMemo, useCallback, useEffect, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  SimpleGrid,
  Divider,
  Link,
  Spinner,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/store/auth'

type TabValue = 'all' | 'trash'

const basePath = '/ticket-management/all-tickets'

type TicketDetail = {
  id: number
  request_number: string
  description: string | null
  contact_number: string | null
  contact_name: string | null
  contact_email: string | null
  ticket_status_id: number
  ticket_status_label?: string
  ticket_status?: { id: number; code: string; label: string }
  service_type_name?: string
  service_type?: { id: number; name: string; code: string }
  submitted_at: string | null
  created_at: string | null
  user?: { id: number; user_login: string; user_email: string }
  assigned_to_user?: { id: number; user_login: string; user_email: string }
  sla?: { id: number; severity: string; response_minutes: number; resolution_minutes: number }
  attachment_metadata?: Array<{ name?: string; file_url?: string }> | null
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

export default function AllTicketsPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure()
  const currentUser = useAuthStore((s) => s.user)
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [selectedUnassignedId, setSelectedUnassignedId] = useState<number | null>(null)
  const [unassignedDetail, setUnassignedDetail] = useState<TicketDetail | null>(null)
  const [unassignedDetailLoading, setUnassignedDetailLoading] = useState(false)
  const [assignToMeLoading, setAssignToMeLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        request_number: { name: 'Request #', withSort: true, maxWidth: '160px' },
        contact_name: { name: 'Contact', withSort: true, maxWidth: '140px' },
        contact_email: { name: 'Email', withSort: true, maxWidth: '180px' },
        ticket_status_label: {
          name: 'Status',
          withSort: true,
          maxWidth: '120px',
          badge: {
            'New': 'blue',
            'Assigned': 'purple',
            'In Progress': 'teal',
            'Pending': 'yellow',
            'Cancelled': 'gray',
            'Resolved': 'green',
            'Closed': 'gray',
            'For Approval': 'orange',
            'Approved': 'green',
            'Rejected': 'red',
          },
        },
        service_type_name: { name: 'Service type', withSort: true, maxWidth: '140px' },
        assigned_to_name: { name: 'Assigned To', withSort: false, maxWidth: '160px' },
        for_approval: {
          name: 'For approval',
          withSort: true,
          maxWidth: '100px',
          badge: { 1: 'orange', 2: 'gray', 3: 'blue' },
          badgeLabels: { 1: 'Yes', 2: 'No', 3: 'Auto' },
        },
        created_at_human: { name: 'Created', withSort: true, maxWidth: '160px' },
      },
      dataSource: activeTab === 'trash' ? `${basePath}/archived` : basePath,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No tickets found',
      hideSelection: false,
      otherActions: [
        {
          name: 'Update status',
          show: (row: { assigned_to: number | null }) => row.assigned_to === currentUser?.id,
          onClick: (row: { id: number }) => navigate(`/ticket-management/all-tickets/${row.id}/update-status`),
        },
      ],
    }),
    [activeTab, currentUser?.id, navigate]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
      canEditRow: (row: { user_id: number | null }) => row.user_id === currentUser?.id,
      canDeleteRow: (row: { user_id: number | null }) => row.user_id === currentUser?.id,
    }),
    [access, currentUser?.id]
  )

  const tableParams = useMemo(() => ({ search: searchQuery, trash: activeTab === 'trash' ? 1 : 0 }), [searchQuery, activeTab])

  const openUnassignedDetail = useCallback((id: number) => {
    setSelectedUnassignedId(id)
    onDetailOpen()
  }, [onDetailOpen])

  useEffect(() => {
    if (!selectedUnassignedId || !isDetailOpen) return
    setUnassignedDetailLoading(true)
    setUnassignedDetail(null)
    apiClient
      .get(`${basePath}/${selectedUnassignedId}`)
      .then(({ data }) => setUnassignedDetail(data.data ?? data))
      .catch((err) => showError(err as Error))
      .finally(() => setUnassignedDetailLoading(false))
  }, [selectedUnassignedId, isDetailOpen, showError])

  const handleAssignToMe = useCallback(async () => {
    if (!selectedUnassignedId || !currentUser?.id) return
    setAssignToMeLoading(true)
    try {
      await apiClient.put(`${basePath}/${selectedUnassignedId}`, { assigned_to: currentUser.id })
      showSuccess('Success', 'Ticket assigned to you')
      onDetailClose()
      setSelectedUnassignedId(null)
      setUnassignedDetail(null)
      tableRef.current?.reload()
    } catch (err) {
      showError(err as Error)
    } finally {
      setAssignToMeLoading(false)
    }
  }, [selectedUnassignedId, currentUser?.id, onDetailClose, showSuccess, showError])

  const customRenderers = useMemo(
    () => ({
      assigned_to_name: (_value: unknown, row: { id: number; assigned_to: number | null; assigned_to_name?: string }) =>
        row.assigned_to == null ? (
          <Button
            variant="link"
            size="sm"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation()
              openUnassignedDetail(row.id)
            }}
          >
            View Details
          </Button>
        ) : (
          row.assigned_to_name ?? '—'
        ),
    }),
    [openUnassignedDetail]
  )

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these tickets. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      if (bulkAction === 'restore') {
        for (const id of selectedIds) {
          await apiClient.put(`${basePath}/restore/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} tickets restored` : 'Ticket restored successfully')
      } else if (bulkAction === 'delete' && activeTab === 'trash') {
        for (const id of selectedIds) {
          await apiClient.delete(`${basePath}/force-delete/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} tickets permanently deleted` : 'Ticket permanently deleted')
      } else if (bulkAction === 'delete' && activeTab === 'all') {
        for (const id of selectedIds) {
          await apiClient.delete(`${basePath}/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} tickets moved to trash` : 'Ticket moved to trash')
      }
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                All Tickets
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                View and manage all ticket requests
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                    <TabList borderBottom="none">
                      <Tab>
                        All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                      </Tab>
                      <Tab>
                        Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                      </Tab>
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname={basePath}
                      createRoute={`${basePath}/create`}
                      createLabel="Create Ticket"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                    </Select>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={handleBulkActionClick}
                      isLoading={isBulkProcessing}
                    >
                      Apply
                    </Button>
                  </HStack>
                )}
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request #, contact, email..."
                      value={searchQuery}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} mx={5} flex={1} overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  setSubSub={setSubSub}
                  access={permissions}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
          <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Confirm</ModalHeader>
              <ModalBody>{modalMessage}</ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
                  Confirm
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="2xl" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent maxH="90vh">
              <ModalHeader>
                Ticket details {unassignedDetail?.request_number && `— ${unassignedDetail.request_number}`}
              </ModalHeader>
              <ModalBody pb={4}>
                {unassignedDetailLoading && (
                  <VStack py={8}>
                    <Spinner size="lg" color="blue.500" />
                    <Text>Loading...</Text>
                  </VStack>
                )}
                {!unassignedDetailLoading && unassignedDetail && (
                  <VStack align="stretch" spacing={4}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Request #</Text>
                        <Text fontWeight="medium">{unassignedDetail.request_number}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Status</Text>
                        <Badge colorScheme="blue">{unassignedDetail.ticket_status?.label ?? unassignedDetail.ticket_status_label ?? '—'}</Badge>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Service type</Text>
                        <Text>{unassignedDetail.service_type?.name ?? unassignedDetail.service_type_name ?? '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Submitted at</Text>
                        <Text>{formatDate(unassignedDetail.submitted_at)}</Text>
                      </Box>
                    </SimpleGrid>

                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1}>Description</Text>
                      <Text whiteSpace="pre-wrap" bg={colors.sectionBg} p={3} borderRadius="md">
                        {unassignedDetail.description || '—'}
                      </Text>
                    </Box>

                    <Divider />
                    <Text fontWeight="semibold" fontSize="sm">Contact</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Name</Text>
                        <Text>{unassignedDetail.contact_name || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Email</Text>
                        <Text>{unassignedDetail.contact_email || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Phone</Text>
                        <Text>{unassignedDetail.contact_number || '—'}</Text>
                      </Box>
                    </SimpleGrid>

                    <Divider />
                    <Text fontWeight="semibold" fontSize="sm">Assignment & SLA</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Submitted by</Text>
                        <Text>{unassignedDetail.user ? `${unassignedDetail.user.user_login} (${unassignedDetail.user.user_email})` : '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Assigned to</Text>
                        <Text>
                          {unassignedDetail.assigned_to_user
                            ? `${unassignedDetail.assigned_to_user.user_login} (${unassignedDetail.assigned_to_user.user_email})`
                            : '—'}
                        </Text>
                      </Box>
                      {unassignedDetail.sla && (
                        <Box>
                          <Text fontSize="xs" color={colors.textMuted}>SLA (severity / response / resolution)</Text>
                          <Text>{unassignedDetail.sla.severity} — {unassignedDetail.sla.response_minutes} min / {unassignedDetail.sla.resolution_minutes} min</Text>
                        </Box>
                      )}
                    </SimpleGrid>

                    {unassignedDetail.attachment_metadata && unassignedDetail.attachment_metadata.length > 0 && (
                      <>
                        <Divider />
                        <Text fontWeight="semibold" fontSize="sm">Attachments</Text>
                        <VStack align="stretch" spacing={1}>
                          {unassignedDetail.attachment_metadata.map((file: { name?: string; file_url?: string }, i: number) => (
                            <Box key={i}>
                              {file.file_url ? (
                                <Link href={file.file_url} isExternal color="blue.500">
                                  {file.name || 'File'}
                                </Link>
                              ) : (
                                <Text>{file.name || 'File'}</Text>
                              )}
                            </Box>
                          ))}
                        </VStack>
                      </>
                    )}

                    <Box>
                      <Text fontSize="xs" color={colors.textMuted}>Created</Text>
                      <Text>{formatDate(unassignedDetail.created_at)}</Text>
                    </Box>
                  </VStack>
                )}
                {!unassignedDetailLoading && !unassignedDetail && selectedUnassignedId && (
                  <Text color={colors.textSecondary}>Could not load ticket details.</Text>
                )}
              </ModalBody>
              {unassignedDetail && !unassignedDetailLoading && (
                <ModalFooter borderTopWidth="1px" borderColor={colors.border}>
                  <Button variant="ghost" mr={3} onClick={onDetailClose}>
                    Close
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleAssignToMe}
                    isLoading={assignToMeLoading}
                    isDisabled={!currentUser?.id}
                  >
                    Assign to me
                  </Button>
                </ModalFooter>
              )}
            </ModalContent>
          </Modal>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
